import { useEffect, useMemo, useState } from 'react';
// import { ValidateSmtpButton } from '../../components/ValidateSmtpButton';
import { NvrFullDto } from '@/lib/integrationTypes/ihub-devices';
import { GroupedSection } from '@/components/ui/grouped-section';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ConfigureAlertingMut, ApplyAlertingConfigMut, AlertingSetupTypesQuery } from '@/lib/api/devices/devices';
import { toast } from '@/hooks/use-toast';
import { Check, KeyRound, Loader2, RefreshCw } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { SMTPCredentials, WebhookCredentials } from '../../components/DeviceCredentials';
import { useReadExternalConnection } from '@/hooks/externalConnectionsApi';
import { CollapsibleSection } from './ConnectionsAndSettingsTab';
import {
  ConfigurationPlan,
  useConfigurationPlanSelection,
  Action,
  ConfigActionGroup,
  ApplyResult,
} from '../../AddDevice/ConfigurationPlan';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

type ExternalDeviceSettingsTabProps = Pick<NvrFullDto, 'id' | 'externalConnectionLimitedDto' | 'smtpUsername' | 'smtpPassword' | 'webhookAuthCredDto'>;

export function ExternalDeviceSettingsTab(props: ExternalDeviceSettingsTabProps) {
  // Track if we've applied changes (to show better "no changes" message)
  const [hasAppliedChanges, setHasAppliedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedSetupType, setSelectedSetupType] = useState<string | undefined>(undefined);

  const autoConfigEnabled = useFeatureFlagEnabled(FeatureFlags.AutoConfig);

  // Eagerly fetch setup types on mount to determine auto-config support
  const setupTypesQuery = useQuery({
    ...AlertingSetupTypesQuery(props.id),
    enabled: !!autoConfigEnabled,
  });

  const supportsAutoConfig = setupTypesQuery.data?.supportsAutoConfig ?? false;
  const setupTypes = useMemo(() => setupTypesQuery.data?.setupTypes ?? [], [setupTypesQuery.data?.setupTypes]);

  // Auto-select when there's exactly one setup type OR a recommended one
  useEffect(() => {
    if (!selectedSetupType && setupTypes.length > 0) {
      const recommended = setupTypes.find(t => t.recommended);
      if (recommended) {
        setSelectedSetupType(recommended.value);
      } else if (setupTypes.length === 1) {
        setSelectedSetupType(setupTypes[0].value);
      }
    }
  }, [setupTypes, selectedSetupType]);

  const configureAlertingMut = useMutation({
    ...ConfigureAlertingMut(props.id),
    onError(error) {
      toast({
        title: 'Failed to fetch configuration',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const applyAlertingMut = useMutation({
    ...ApplyAlertingConfigMut(props.id),
    onSuccess() {
      // Mark that we've applied changes (for better "no changes" message after refresh)
      setHasAppliedChanges(true);
    },
    onError(error) {
      // Only show toast for server errors (500, network errors, etc.)
      toast({
        title: 'Server error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const applyResults: ApplyResult[] | undefined = applyAlertingMut.data?.results;

  const configGroups: ConfigActionGroup[] = useMemo(() => {
    if (!configureAlertingMut.data?.plan?.groups) return [];
    return configureAlertingMut.data.plan.groups.map((group) => ({
      groupName: group.groupName,
      key: group.key,
      description: group.description,
      warning: group.warning,
      actions: group.actions.map((action) => ({
        field: action.field,
        key: action.key,
        action: Action[action.action as keyof typeof Action],
        currentValue: action.currentValue,
        proposedValue: action.proposedValue,
        description: action.description,
        warning: action.warning,
      })),
    }));
  }, [configureAlertingMut.data]);

  const {
    selectedActions,
    handleSelectionChange,
    handleGroupSelectionChange,
  } = useConfigurationPlanSelection(configGroups);


  const getSelectedChangeIds = (): string[] => {
    const changeIds: string[] = [];
    configGroups.forEach((group) => {
      const selectedKeys = selectedActions.get(group.groupName);
      if (selectedKeys) {
        group.actions.forEach((action) => {
          if (selectedKeys.has(action.key)) {
            changeIds.push(`${group.key}.${action.key}`);
          }
        });
      }
    });
    return changeIds;
  };

  const hasFetched = configureAlertingMut.data !== undefined;

  const extConnQuery = useReadExternalConnection({ id: props.externalConnectionLimitedDto?.id ?? '' });
  const credDevice = extConnQuery.data?.externalConnection || props;
  const hasSMTP = credDevice.smtpUsername && credDevice.smtpPassword;
  const hasWebhook = credDevice.webhookAuthCredDto;

  return (
    <>
    <div className="mx-auto max-w-screen-2xl p-6 space-y-6">
      {autoConfigEnabled && supportsAutoConfig && (
        <GroupedSection
          title="Configure Device"
          description="Select a setup type and fetch the configuration to review proposed changes for this device."
          vertical
        >
        <div className="space-y-4">

          {/* Setup type selection - only show when there are multiple options */}
          {setupTypes.length > 1 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300 block">
                Setup Type
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                {setupTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex flex-col cursor-pointer rounded-lg border p-4 ${
                      selectedSetupType === type.value
                        ? 'border-lime bg-lime/10'
                        : 'border-gray-700'
                    }`}
                    onClick={() => setSelectedSetupType(type.value)}
                  >
                    <div className="flex w-full items-center">
                      <input
                        type="radio"
                        name="setupType"
                        checked={selectedSetupType === type.value}
                        onChange={() => {}}
                        className="sr-only"
                      />
                      <span className="font-semibold text-lime">{type.label}</span>
                      {type.recommended && (
                        <Badge variant="secondary" className="ml-2 bg-lime/20 text-lime hover:bg-lime/30 border-lime/20">
                          Recommended
                        </Badge>
                      )}
                    </div>
                    {type.description && (
                      <div className="mt-2 text-xs text-gray-400">{type.description}</div>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => {
                setHasAppliedChanges(false);
                applyAlertingMut.reset();
                configureAlertingMut.mutate(selectedSetupType);
              }}
              disabled={configureAlertingMut.isPending || (setupTypes.length > 1 && !selectedSetupType)}
            >
              {configureAlertingMut.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Fetching configuration...
                </>
              ) : hasFetched ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh configuration
                </>
              ) : (
                'Fetch configuration'
              )}
            </Button>
          </div>

          {/* Show loading state for setup types query */}
          {setupTypesQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading configuration options...
            </div>
          )}

          {configureAlertingMut.data && (
            <div className="mt-4 space-y-4">
              {/* Display API errors */}
              {configureAlertingMut.data.errors && configureAlertingMut.data.errors.length > 0 && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                  <p className="text-sm font-medium text-red-400">Failed to fetch configuration from the device.</p>
                </div>
              )}

              {/* Display API message */}
              {configureAlertingMut.data.message && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
                  <p className="text-sm text-yellow-400">{configureAlertingMut.data.message}</p>
                </div>
              )}

              {configGroups.length > 0 ? (
                <>
                  <ConfigurationPlan
                    groups={configGroups}
                    selectedActions={selectedActions}
                    onSelectionChange={handleSelectionChange}
                    onGroupSelectionChange={handleGroupSelectionChange}
                    isApplying={applyAlertingMut.isPending}
                    applyResults={applyResults}
                    disabled={hasAppliedChanges}
                  />
                  <div className="flex justify-end pt-4 border-t border-white/10">
                    <Button
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={applyAlertingMut.isPending || getSelectedChangeIds().length === 0 || hasAppliedChanges}
                    >
                      {applyAlertingMut.isPending ? 'Applying...' : 'Apply configuration'}
                    </Button>
                  </div>
                </>
              ) : (
                // Improved "no changes" message - show success state if we just applied changes
                <div className={`rounded-lg border p-4 flex items-center gap-3 ${
                  hasAppliedChanges 
                    ? 'border-green-500/30 bg-green-500/10' 
                    : 'border-white/10 bg-white/5'
                }`}>
                  {hasAppliedChanges ? (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20">
                        <Check className="h-4 w-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-green-400">Device is fully configured</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          All settings match the required configuration. No changes needed.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-500/20">
                        <Check className="h-4 w-4 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-300">No configuration changes needed</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          The device settings already match the required configuration.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </GroupedSection>
      )}

      {(hasSMTP || hasWebhook) && (
        <CollapsibleSection
          key={String(setupTypesQuery.isPending)}
          title="Device Credentials"
          description="Credentials used to authenticate with the device"
          icon={<KeyRound className="h-5 w-5 text-muted-foreground" />}
          defaultOpen={!supportsAutoConfig}
        >
          <div className="space-y-4">
            {hasSMTP && <SMTPCredentials device={credDevice} />}
            {hasWebhook && <WebhookCredentials device={credDevice} />}
          </div>
        </CollapsibleSection>
      )}
    </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Configuration Changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to apply these configuration changes? This will modify settings directly on your device.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="default"
              disabled={applyAlertingMut.isPending}
              onClick={() => {
                applyAlertingMut.mutate(getSelectedChangeIds());
                setShowConfirmDialog(false);
              }}
            >
              {applyAlertingMut.isPending ? 'Applying...' : 'Apply Changes'}
            </Button>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={applyAlertingMut.isPending}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
